import { EMPTY, Observable, of } from 'rxjs';
import { KnowledgeBody, KnowledgeCategory, KnowledgeId, KnowledgeQuotationTargetId, KnowledgeTitle, KnowledgeType } from '../../value';
import { distinct, expand, filter, map, mergeMap, take } from 'rxjs/operators';

import { AccountOrganizationId } from '../../../account/value';
import { FirestoreQueryBuilder } from '../../../../../lib/gcp/builder/firestore-query.builder';
import { IFirestoreService } from '../../../../../lib/gcp/service/firestore.service';
import { IKnowledgeRepository } from '../../repository';
import { IsDeleted } from '../../../base/value';
import { Knowledge } from '../../entity';
import { KnowledgeNotFoundError } from '../../exception';
import { Timestamp } from '../../../../../utility/model/timestamp.value';

export class KnowledgeFirestoreRepository implements IKnowledgeRepository {
  private static readonly collectionId = 'knowledge';

  constructor(private readonly firestoreService: IFirestoreService) { }

  select(id: KnowledgeId): Observable<Knowledge> {
    return this.firestoreService.getDocument(KnowledgeFirestoreRepository.collectionId, id).pipe(
      map(item => {
        if (!item || item.isDeleted === true) {
          throw new KnowledgeNotFoundError(`knowledge id「${id.value}」 is not found`);
        }
        return this.convertToEntity(item);
      })
    );
  }

  selectForUpdateEs(id: KnowledgeId): Observable<Knowledge> | null {
    return this.firestoreService.getDocument(KnowledgeFirestoreRepository.collectionId, id).pipe(
      map(item => item ? this.convertToEntity(item) : null)
    );
  }

  selectAll(builder: FirestoreQueryBuilder<Knowledge>): Observable<Knowledge> {
    return this.firestoreService.getCollection(KnowledgeFirestoreRepository.collectionId, builder).pipe(
      take(1),
      expand(items =>
        items.length
          ? this.firestoreService
            .getCollection(KnowledgeFirestoreRepository.collectionId, builder.startAfter(items[items.length - 1].id).limit(100))
            .pipe(take(1))
          : EMPTY
      ),
      mergeMap(items => items),
      distinct(item => item.id),
      filter(item => item.isDeleted !== true),
      map(item => this.convertToEntity(item))
    );
  }

  insert(item: Knowledge): Observable<Knowledge> {
    item.createdAt = Timestamp.createByDate(new Date());
    item.updatedAt = Timestamp.createByDate(new Date());
    item.isDeleted = IsDeleted.create(false);
    return this.firestoreService.setDocument(KnowledgeFirestoreRepository.collectionId, this.convertToMap(item)).pipe(map(() => item));
  }

  update(knowledge: Knowledge): Observable<Knowledge> {
    knowledge.updatedAt = Timestamp.createByDate(new Date());
    knowledge.isDeleted = IsDeleted.create(false);
    return this.firestoreService.getDocument(KnowledgeFirestoreRepository.collectionId, knowledge.id).pipe(
      take(1),
      mergeMap(item => {
        if (!item || item && item.isDeleted === true) {
          throw new KnowledgeNotFoundError(`knowledge id「${knowledge.id.value}」 is not found`);
        }
        return this.firestoreService.setDocument(KnowledgeFirestoreRepository.collectionId, this.convertToMap(knowledge));
      }),
      map(_ => knowledge)
    );
  }

  delete(id: KnowledgeId) {
    const proposal = new Knowledge(id);
    proposal.isDeleted = IsDeleted.create(true);
    proposal.updatedAt = Timestamp.createByMillsec(+Date.now());
    return this.firestoreService.setDocument(KnowledgeFirestoreRepository.collectionId, this.convertToMap(proposal));
  }

  generateId(): KnowledgeId {
    return KnowledgeId.create(this.firestoreService.generateId());
  }

  private convertToMap(knowledge: Knowledge): object {
    return Knowledge.allFields.reduce((p, key) => {
      if (knowledge[key] === undefined) {
        return p;
      }
      const value = knowledge[key] as { value: any };
      p[key] = value.value;
      return p;
    }, {});
  }

  private convertToEntity(item: any) {
    const knowledge = new Knowledge(KnowledgeId.create(item.id));
    knowledge.registeredBy = AccountOrganizationId.create(item.registeredBy);
    knowledge.sendBy = AccountOrganizationId.create(item.sendBy);
    knowledge.title = KnowledgeTitle.create(item.title);
    knowledge.body = KnowledgeBody.create(item.body);
    knowledge.type = KnowledgeType.create(item.type);
    knowledge.category = item && item.category ? KnowledgeCategory.create(item.category) : undefined;
    knowledge.quotationTargetId = item.quotationTargetId ? KnowledgeQuotationTargetId.create(item.quotationTargetId) : undefined;
    knowledge.wroteAt = Timestamp.createByMillsec(item.wroteAt.seconds * 1000);
    knowledge.createdAt = Timestamp.createByMillsec(item.createdAt.seconds * 1000);
    knowledge.updatedAt = Timestamp.createByMillsec(item.updatedAt.seconds * 1000);
    knowledge.isDeleted = item && item.isDeleted ? IsDeleted.create(item.isDeleted) : IsDeleted.create(false);
    return knowledge;
  }
}
